import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Globe, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "../ProjectCard";
import { fmtMonth } from "../../lib/format";
import {
  adminListProjects, adminCreateProject, adminUpdateProject, adminDeleteProject,
} from "../../lib/api";

const EMPTY_FORM = {
  title: "", description: "", tech: "", status: "draft",
  date: "", github_url: "", demo_url: "", image_url: "", featured: false,
};

function toForm(p) {
  return {
    title: p.title, description: p.description, tech: (p.tech || []).join(", "),
    status: p.status, date: p.date || "", github_url: p.github_url || "",
    demo_url: p.demo_url || "", image_url: p.image_url || "", featured: !!p.featured,
  };
}

function toPayload(form) {
  return {
    title: form.title.trim(),
    description: form.description.trim(),
    tech: form.tech.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 12),
    status: form.status,
    date: form.date,
    github_url: form.github_url.trim() || null,
    demo_url: form.demo_url.trim() || null,
    image_url: form.image_url.trim() || null,
    featured: form.featured,
  };
}

export function ProjectsTab({ onMutate }) {
  const [projects, setProjects] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // project being edited, or null for new
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = () =>
    adminListProjects().then(setProjects).catch(() => toast.error("Could not load projects"));

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm(toForm(p)); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await adminUpdateProject(editing.id, toPayload(form));
        toast.success("Project updated");
      } else {
        await adminCreateProject(toPayload(form));
        toast.success("Project created as draft");
      }
      setDialogOpen(false);
      await load();
      onMutate?.();
    } catch {
      toast.error("Save failed — check the fields and try again");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (p) => {
    try {
      await adminUpdateProject(p.id, { ...toPayload(toForm(p)), status: p.status === "published" ? "draft" : "published" });
      toast.success(p.status === "published" ? "Unpublished — hidden from public" : "Published — now live on the site");
      await load();
      onMutate?.();
    } catch {
      toast.error("Could not change status");
    }
  };

  const remove = async (p) => {
    try {
      await adminDeleteProject(p.id);
      toast.success("Project deleted");
      setConfirmDeleteId(null);
      await load();
      onMutate?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const inputCls = "w-full px-3 py-2 rounded bg-deep border border-white/10 text-white text-xs font-mono focus:outline-none focus:border-cyber";

  return (
    <div data-testid="admin-projects-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="font-mono text-xs text-dim">Only PUBLISHED projects appear on the public site.</p>
        <button type="button" onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 rounded bg-cyber text-[#0b0f19] font-bold text-xs font-mono hover:bg-white transition-colors"
          data-testid="admin-new-project-button">
          <Plus className="w-3.5 h-3.5" /> New Project
        </button>
      </div>

      {projects === null ? (
        <p className="font-mono text-xs text-dim">loading ...</p>
      ) : projects.length === 0 ? (
        <p className="font-mono text-xs text-dim p-6 rounded bg-panelalt border border-white/5" data-testid="admin-projects-empty">
          No projects yet. Create your first draft with the button above.
        </p>
      ) : (
        <div className="space-y-3" data-testid="admin-projects-list">
          {projects.map((p) => (
            <div key={p.id} className="p-4 rounded bg-panelalt border border-white/5 flex flex-col md:flex-row md:items-center gap-3 justify-between" data-testid={`admin-project-row-${p.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-mono text-sm text-white font-bold">{p.title}</span>
                  <StatusBadge status={p.status} />
                  {p.sample && (
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 font-bold" data-testid={`sample-badge-${p.id}`}>SAMPLE</span>
                  )}
                  {p.featured && (
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-cyber/15 text-cyber font-bold">FEATURED</span>
                  )}
                </div>
                <p className="font-mono text-[11px] text-dim truncate">{p.description}</p>
                <p className="font-mono text-[10px] text-dim mt-1">{fmtMonth(p.date)} • {(p.tech || []).join(" • ") || "no tags"}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={() => togglePublish(p)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded font-mono text-[11px] font-bold transition-colors ${
                    p.status === "published"
                      ? "bg-white/10 text-dim hover:text-white"
                      : "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                  }`}
                  data-testid={`admin-toggle-publish-${p.id}`}>
                  {p.status === "published" ? <><EyeOff className="w-3 h-3" /> Unpublish</> : <><Globe className="w-3 h-3" /> Publish</>}
                </button>
                <button type="button" onClick={() => openEdit(p)} aria-label="Edit project"
                  className="p-2 rounded bg-white/5 text-dim hover:text-cyber transition-colors" data-testid={`admin-edit-project-${p.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                {confirmDeleteId === p.id ? (
                  <button type="button" onClick={() => remove(p)}
                    className="px-3 py-1.5 rounded bg-red-500/20 text-red-400 font-mono text-[11px] font-bold hover:bg-red-500/30 transition-colors"
                    data-testid={`admin-confirm-delete-${p.id}`}>
                    Confirm?
                  </button>
                ) : (
                  <button type="button" onClick={() => setConfirmDeleteId(p.id)} aria-label="Delete project"
                    className="p-2 rounded bg-white/5 text-dim hover:text-red-400 transition-colors" data-testid={`admin-delete-project-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-panel border-white/10 text-mist max-w-lg max-h-[85vh] overflow-y-auto" data-testid="project-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white uppercase">{editing ? "Edit Project" : "New Project"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="block font-mono text-xs text-dim mb-1" htmlFor="pf-title">Title *</label>
              <input id="pf-title" className={inputCls} value={form.title} maxLength={120}
                onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="project-form-title" />
            </div>
            <div>
              <label className="block font-mono text-xs text-dim mb-1" htmlFor="pf-desc">Short description</label>
              <textarea id="pf-desc" rows={3} className={inputCls} value={form.description} maxLength={600}
                onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-form-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-mono text-xs text-dim mb-1">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-deep border-white/10 text-white text-xs font-mono" data-testid="project-form-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-panel border-white/10 text-mist">
                    <SelectItem value="draft" data-testid="status-option-draft">Draft (private)</SelectItem>
                    <SelectItem value="in_progress" data-testid="status-option-in-progress">In Progress (private)</SelectItem>
                    <SelectItem value="published" data-testid="status-option-published">Published (public)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block font-mono text-xs text-dim mb-1" htmlFor="pf-date">Date</label>
                <input id="pf-date" type="month" className={inputCls} value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="project-form-date" />
              </div>
            </div>
            <div>
              <label className="block font-mono text-xs text-dim mb-1" htmlFor="pf-tech">Technologies (comma separated)</label>
              <input id="pf-tech" className={inputCls} placeholder="Python, Linux, Wireshark" value={form.tech}
                onChange={(e) => setForm({ ...form, tech: e.target.value })} data-testid="project-form-tech" />
            </div>
            <div>
              <label className="block font-mono text-xs text-dim mb-1" htmlFor="pf-github">GitHub URL (optional)</label>
              <input id="pf-github" className={inputCls} placeholder="https://github.com/oVu-Lab/..." value={form.github_url}
                onChange={(e) => setForm({ ...form, github_url: e.target.value })} data-testid="project-form-github" />
            </div>
            <div>
              <label className="block font-mono text-xs text-dim mb-1" htmlFor="pf-image">Image URL (optional)</label>
              <input id="pf-image" className={inputCls} placeholder="https://..." value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="project-form-image" />
            </div>
            <label className="flex items-center gap-2 font-mono text-xs text-dim cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="accent-[#38bdf8]" data-testid="project-form-featured" />
              Feature on the home page
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setDialogOpen(false)}
                className="px-4 py-2 rounded border border-white/20 text-dim font-mono text-xs hover:text-white transition-colors"
                data-testid="project-form-cancel">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-5 py-2 rounded bg-cyber text-[#0b0f19] font-bold font-mono text-xs hover:bg-white transition-colors disabled:opacity-50"
                data-testid="project-form-save">
                {saving ? "Saving..." : "Save Project"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
